import { useMemo } from "react";
import { useScheduling } from "@/data/scheduling/context";
import { FEATURE_SCHEDULING } from "@/data/scheduling/context";
import { useRole } from "@/data/RoleContext";
import { ROLE_CALENDAR_DEFAULTS } from "@/data/scheduling/roleDefaults";
import { EVENT_TYPE_LABELS, DEPARTMENT_COLORS, EVENT_TYPE_DEPARTMENT } from "@/data/scheduling/types";
import type { EventType } from "@/data/scheduling/types";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight, Clock } from "lucide-react";
import { format, parseISO, isToday, isTomorrow, isAfter } from "date-fns";
import { formatSafe } from "@/lib/dateUtils";

interface MyScheduleWidgetProps {
  /** Max number of events to show (default 5) */
  maxItems?: number;
  /** Compact layout for tighter spaces */
  compact?: boolean;
}

export function MyScheduleWidget({ maxItems = 5, compact = false }: MyScheduleWidgetProps) {
  const { events } = useScheduling();
  const { role } = useRole();
  const roleDefaults = ROLE_CALENDAR_DEFAULTS[role];

  const upcomingEvents = useMemo(() => {
    if (!FEATURE_SCHEDULING) return [];
    const now = new Date();
    const relevantTypes = new Set<EventType>(roleDefaults.eventTypes);

    return events
      .filter(e => {
        if (!relevantTypes.has(e.eventType)) return false;
        const start = parseISO(e.startTime);
        if (!isAfter(start, new Date(now.getFullYear(), now.getMonth(), now.getDate()))) return false;
        if (!isToday(start) && !isTomorrow(start)) return false;
        if (e.status === "cancelled") return false;
        return true;
      })
      .sort((a, b) => parseISO(a.startTime).getTime() - parseISO(b.startTime).getTime())
      .slice(0, maxItems);
  }, [events, roleDefaults.eventTypes, maxItems]);

  if (!FEATURE_SCHEDULING || upcomingEvents.length === 0) return null;

  const todayCount = upcomingEvents.filter(e => isToday(parseISO(e.startTime))).length;
  const tomorrowCount = upcomingEvents.filter(e => isTomorrow(parseISO(e.startTime))).length;

  return (
    <Card className="shadow-sm">
      <CardContent className={compact ? "p-3" : "p-4"}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center">
              <CalendarDays className="h-3.5 w-3.5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-bold tracking-tight">My Schedule</h3>
              <p className="text-[11px] text-muted-foreground">
                {todayCount > 0 && `${todayCount} today`}
                {todayCount > 0 && tomorrowCount > 0 && " · "}
                {tomorrowCount > 0 && `${tomorrowCount} tomorrow`}
              </p>
            </div>
          </div>
          <Link
            to="/scheduling"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            Full Calendar <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="space-y-1.5">
          {upcomingEvents.map(event => {
            const start = parseISO(event.startTime);
            const end = parseISO(event.endTime);
            const dept = EVENT_TYPE_DEPARTMENT[event.eventType];
            const deptColor = DEPARTMENT_COLORS[dept];
            const dayLabel = isToday(start) ? "Today" : "Tomorrow";

            return (
              <div
                key={event.id}
                className="flex items-center gap-2.5 rounded-md border border-border/50 p-2.5 hover:bg-muted/30 transition-colors"
              >
                {/* Department color indicator */}
                <div
                  className="h-8 w-1 rounded-full shrink-0"
                  style={{ backgroundColor: deptColor }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-semibold truncate">{event.title}</span>
                    <Badge variant="outline" className="text-[9px] shrink-0">
                      {EVENT_TYPE_LABELS[event.eventType]}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" />
                      {formatSafe(start, "h:mm a")} – {formatSafe(end, "h:mm a")}
                    </span>
                    <span className={`text-[10px] font-medium ${isToday(start) ? "text-primary" : "text-muted-foreground"}`}>
                      {dayLabel}
                    </span>
                  </div>
                  {event.crewName && (
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">
                      {event.crewName}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
